import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The prose anchor — the single source of truth for an in-copy internal link.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
 * `font-medium text-primary underline underline-offset-2 hover:no-underline` was
 * the most-repeated className in the repo: **225 sites across 46 files** (measured
 * 2026-08-13, `.tsx` under `app/` + `components/`). Every one of them is the same
 * decision — "this is a link inside body copy" — retyped. A design tweak to that
 * treatment (a colour, an offset, a hover behaviour) meant 225 edits, and the
 * cross-linking snippet in `.claude/rules/storefront.md` invited a 226th every
 * time someone added a page.
 *
 * The extraction is safe precisely because the output is byte-identical by
 * construction: ONE className, emitted from ONE place. `PROSE_LINK_CLASS` is the
 * literal, and `ProseLink` is the `next/link` wrapper around it.
 *
 * ── WHY THE TOKEN ORDER IS LOAD-BEARING ──────────────────────────────────────
 * `cn` is `twMerge(clsx(...))`, so the emitted class STRING — order included — is
 * part of the rendered HTML, and these are crawled pages pinned by committed DOM
 * baselines under `tests/baselines/dom/`. The extra classes therefore come FIRST
 * and the anchor tokens LAST, matching the four call sites that carried a prefix
 * (`mt-3 inline-flex items-center text-sm …` in AreaLandingPage, `shrink-0
 * text-xs …` in CollectionPointRecap). Both were measured through `twMerge` and
 * round-trip unchanged. Flipping the argument order, or dropping `cn`, changes
 * bytes on a live page — see PROSELINK-4/5.
 *
 * ── SERVER-RENDERABLE, DELIBERATELY ──────────────────────────────────────────
 * NO `"use client"`. Every consumer is an SSR'd, crawled storefront page; a
 * client boundary here would pull the surrounding prose into the client bundle
 * and is exactly the server/client trap `.claude/rules/code-style.md` describes.
 * Keep the import list to `next/link` + `@/lib/utils`.
 *
 * ── WHAT THIS IS *NOT* FOR ───────────────────────────────────────────────────
 * Native `<a>` (external `target="_blank"` links) and `<button>` keep their own
 * element — `next/link` emits `class` BEFORE `href`, while a hand-written
 * `<a href … className>` emits `href` first, so swapping the element changes the
 * markup. Those sites import `PROSE_LINK_CLASS` instead, which keeps the literal
 * in one place without touching a byte of output.
 */
export const PROSE_LINK_CLASS =
  "font-medium text-primary underline underline-offset-2 hover:no-underline";

export function ProseLink({
  href,
  className,
  children,
}: {
  href: string;
  /** Extra classes. Emitted BEFORE the anchor tokens — see the note above. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link href={href} className={cn(className, PROSE_LINK_CLASS)}>
      {children}
    </Link>
  );
}
